/**
 * VÉRAULT · Concierge real-time helpers (Wave 11k + 11q)
 * ────────────────────────────────────────────────────────────────────
 * Wave 11q additions:
 *   • Notification preferences cache (sound/browser/quiet hours/mute).
 *   • Quiet Hours window check (LOCAL time, supports crossing midnight).
 *   • Per-conversation mute respected before firing chime/notification.
 */
import { useCallback, useEffect, useRef, useState } from "react";
import { api } from "../index";

// ─── Notification preferences cache ────────────────────────────────
// Read once on mount, kept fresh whenever Settings updates them.
let _prefsCache = null;
const _prefsSubscribers = new Set();

export async function loadNotificationPrefs() {
  try {
    const { data } = await api.get("/me/notification-preferences");
    _prefsCache = data || {};
  } catch {
    _prefsCache = {
      sound_enabled: true,
      browser_enabled: true,
      quiet_hours_enabled: false,
      quiet_hours_start: "22:00",
      quiet_hours_end: "07:00",
      muted_conversations: [],
    };
  }
  _prefsSubscribers.forEach((cb) => cb(_prefsCache));
  return _prefsCache;
}

export function getNotificationPrefs() {
  return _prefsCache || {
    sound_enabled: true,
    browser_enabled: true,
    quiet_hours_enabled: false,
    quiet_hours_start: "22:00",
    quiet_hours_end: "07:00",
    muted_conversations: [],
  };
}

export function useNotificationPrefs() {
  const [prefs, setPrefs] = useState(_prefsCache);
  useEffect(() => {
    if (!_prefsCache) loadNotificationPrefs().then(setPrefs);
    else setPrefs(_prefsCache);
    const cb = (p) => setPrefs({ ...p });
    _prefsSubscribers.add(cb);
    return () => _prefsSubscribers.delete(cb);
  }, []);
  return prefs || getNotificationPrefs();
}

export async function updateNotificationPrefs(patch) {
  try {
    const { data } = await api.patch("/me/notification-preferences", patch);
    _prefsCache = data || _prefsCache;
    _prefsSubscribers.forEach((cb) => cb(_prefsCache));
    return _prefsCache;
  } catch {
    return _prefsCache;
  }
}

export async function toggleConversationMute(conversationId, muted) {
  try {
    const { data } = await api.post("/me/notification-preferences/mute", {
      conversation_id: conversationId,
      muted: !!muted,
    });
    _prefsCache = data || _prefsCache;
    _prefsSubscribers.forEach((cb) => cb(_prefsCache));
    return _prefsCache;
  } catch {
    return _prefsCache;
  }
}

/** Returns true if NOW (browser-local) is inside the user's quiet hours window. */
function _isInQuietHours(prefs, now = new Date()) {
  if (!prefs?.quiet_hours_enabled) return false;
  const parse = (s) => {
    const m = /^(\d{2}):(\d{2})$/.exec(s || "");
    if (!m) return null;
    return parseInt(m[1], 10) * 60 + parseInt(m[2], 10);
  };
  const start = parse(prefs.quiet_hours_start);
  const end = parse(prefs.quiet_hours_end);
  if (start == null || end == null) return false;
  const cur = now.getHours() * 60 + now.getMinutes();
  // Crosses midnight (e.g. 22:00 → 07:00)
  if (start > end) return cur >= start || cur < end;
  return cur >= start && cur < end;
}

// ─── 1. Throttled typing heartbeat ────────────────────────────────────
const TYPING_THROTTLE_MS = 3000;

export function useTypingHeartbeat({ role, conversationId, disabled = false }) {
  const lastSentRef = useRef(0);

  const path = role === "client"
    ? (id) => `/client-portal/concierge/conversations/${id}/typing`
    : (id) => `/concierge/conversations/${id}/typing`;

  const ping = useCallback(() => {
    if (disabled || !conversationId) return;
    const now = Date.now();
    if (now - lastSentRef.current < TYPING_THROTTLE_MS) return;
    lastSentRef.current = now;
    api.post(path(conversationId)).catch(() => {
      // Typing heartbeats are best-effort; never surface errors.
    });
  }, [conversationId, disabled, path]);

  return ping;
}

// ─── 2. Soft airline seatbelt chime via Web Audio ─────────────────────
let _audioCtx = null;
function getAudioCtx() {
  if (typeof window === "undefined") return null;
  if (_audioCtx) return _audioCtx;
  const Ctor = window.AudioContext || window.webkitAudioContext;
  if (!Ctor) return null;
  try { _audioCtx = new Ctor(); } catch { _audioCtx = null; }
  return _audioCtx;
}

/**
 * Plays a calm two-tone "ding-dong" reminiscent of an airline seatbelt
 * release chime. Total ~0.9s, peak volume well below speaking level.
 */
export function playChime() {
  const ctx = getAudioCtx();
  if (!ctx) return;
  // Resume context if it was suspended (mobile autoplay policies).
  if (ctx.state === "suspended") {
    try { ctx.resume(); } catch { /* ignore */ }
  }
  const now = ctx.currentTime;
  // Two tones: 660 Hz then 510 Hz, each ~0.45s with a soft envelope.
  const tones = [
    { freq: 660, start: 0,    dur: 0.55 },
    { freq: 510, start: 0.34, dur: 0.65 },
  ];
  tones.forEach(({ freq, start, dur }) => {
    const osc = ctx.createOscillator();
    const gain = ctx.createGain();
    osc.type = "sine";
    osc.frequency.value = freq;
    // Soft attack/decay envelope — never harsh.
    gain.gain.setValueAtTime(0.0001, now + start);
    gain.gain.exponentialRampToValueAtTime(0.18, now + start + 0.04);
    gain.gain.exponentialRampToValueAtTime(0.0001, now + start + dur);
    osc.connect(gain).connect(ctx.destination);
    osc.start(now + start);
    osc.stop(now + start + dur + 0.05);
  });
}

/**
 * Some mobile browsers require a user gesture before any audio can play.
 * Call this once after the first user interaction (e.g. clicking the
 * Composer textarea) to "warm up" the audio context. Safe to no-op.
 */
export function primeAudio() {
  const ctx = getAudioCtx();
  if (!ctx) return;
  if (ctx.state === "suspended") {
    try { ctx.resume(); } catch { /* ignore */ }
  }
}

// ─── 3. New-message notification orchestration ────────────────────────
let _permissionRequested = false;

async function ensureNotificationPermission() {
  if (typeof window === "undefined") return "denied";
  if (!("Notification" in window)) return "unsupported";
  if (Notification.permission === "granted") return "granted";
  if (Notification.permission === "denied") return "denied";
  if (_permissionRequested) return Notification.permission;
  _permissionRequested = true;
  try {
    const result = await Notification.requestPermission();
    return result;
  } catch {
    return "denied";
  }
}

function flashTitle(label) {
  if (typeof document === "undefined") return () => {};
  const original = document.title;
  let on = true;
  const iv = setInterval(() => {
    document.title = on ? `${label} · ${original}` : original;
    on = !on;
  }, 1400);
  const stop = () => {
    clearInterval(iv);
    document.title = original;
    document.removeEventListener("visibilitychange", onVis);
  };
  const onVis = () => { if (!document.hidden) stop(); };
  document.addEventListener("visibilitychange", onVis);
  return stop;
}

/**
 * useChatNotifications
 *   messages       — current message list (must be ordered ASC by created_at)
 *   myRole         — "advisor" | "client" — used to suppress self-notify
 *   senderLabel    — string shown in the Notification title (e.g. "Jonathan")
 *   bodyPreview    — optional (msg) => string to override preview body
 *   onlyWhenHidden — default true; sound + Notification only when tab in background
 */
export function useChatNotifications({
  messages,
  myRole,
  senderLabel = "Verault Concierge",
  bodyPreview,
  onlyWhenHidden = true,
  conversationId,  // Wave 11q — required for per-conversation mute
}) {
  const lastSeenIdRef = useRef(null);
  const titleStopRef = useRef(null);

  // Wave 11q — Lazy-load prefs on first mount.
  useEffect(() => {
    if (!_prefsCache) loadNotificationPrefs();
  }, []);

  // Ask permission once, lazily.
  useEffect(() => {
    ensureNotificationPermission();
    return () => {
      if (titleStopRef.current) {
        titleStopRef.current();
        titleStopRef.current = null;
      }
    };
  }, []);

  useEffect(() => {
    if (!Array.isArray(messages) || messages.length === 0) {
      lastSeenIdRef.current = null;
      return;
    }
    const latest = messages[messages.length - 1];
    const first = lastSeenIdRef.current === null;
    if (first) {
      lastSeenIdRef.current = latest.id;
      return;
    }
    if (latest.id === lastSeenIdRef.current) return;
    lastSeenIdRef.current = latest.id;

    const fromOther = latest.sender_role && latest.sender_role !== myRole;
    if (!fromOther) return;

    const hidden = typeof document !== "undefined" && document.hidden;
    if (onlyWhenHidden && !hidden) return;

    // Wave 11q — gate on preferences (sound + browser + quiet hours +
    // per-conversation mute).
    const prefs = getNotificationPrefs();
    const muted = (prefs.muted_conversations || []).includes(conversationId);
    if (muted) return;
    if (_isInQuietHours(prefs)) return;

    if (prefs.sound_enabled !== false) {
      try { playChime(); } catch { /* ignore */ }
    }

    if (hidden && !titleStopRef.current) {
      titleStopRef.current = flashTitle("New message");
    }

    if (prefs.browser_enabled === false) return;

    (async () => {
      const perm = await ensureNotificationPermission();
      if (perm !== "granted") return;
      try {
        const preview = bodyPreview
          ? bodyPreview(latest)
          : (latest.body || (latest.attachments?.length ? "Attachment" : "New message"));
        const n = new Notification(senderLabel, {
          body: preview.length > 140 ? preview.slice(0, 137) + "…" : preview,
          tag: `verault-concierge-${latest.conversation_id || conversationId || "global"}`,
          renotify: true,
          silent: true,
          icon: "/favicon.ico",
        });
        n.onclick = () => {
          window.focus();
          n.close();
        };
      } catch { /* ignore */ }
    })();
  }, [messages, myRole, senderLabel, bodyPreview, onlyWhenHidden, conversationId]);
}
